import { getCurrencySymbol } from "@tellescope/utils";
import {
  Button,
  CurrencyInput,
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tab,
  TabbedContainer,
  TabsContent,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { toast } from "sonner";

interface ChargePaymentFormProps {
  contactId: string;
  onSubmit?: () => void;
  onCancel?: () => void;
}

export const ChargePaymentForm = ({ contactId, onSubmit, onCancel }: ChargePaymentFormProps) => {
  const { t } = useTranslation("payments");

  const stripeAccountsQuery = useTellescopeInfiniteQuery({
    model: "organizations",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flatMap((page) => page.flatMap((org) => org.stripeKeyDetails ?? [])),
  });

  const productsQuery = useTellescopeInfiniteQuery({
    model: "products",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const chargePaymentMutation = useTellescopeMutation({
    model: "purchases",
    method: "charge_card_on_file",
    autoCacheUpdateStrategy: "invalidate",
    onSuccess: () => {
      onSubmit?.();
    },
    onError: () => {
      toast.error(t("There was an error charging the card on file."));
    },
  });

  const tabs: Tab[] = [
    { label: t("Products"), value: t("Products") },
    { label: t("Custom Price"), value: t("Custom Price") },
  ];

  const hasProducts = (productsQuery.data?.length ?? 0) > 0;
  const form = useForm({
    fields: {
      stripeAccountKey: {
        type: "select",
        initialValue: stripeAccountsQuery.data?.length === 1 ? stripeAccountsQuery.data[0]?.key : "",
        required: true,
      },
      type: {
        type: "select",
        initialValue: hasProducts ? "products" : "customPrice",
        required: true,
      },
      productIds: {
        type: "multiselect",
        initialValue: productsQuery.data?.length === 1 ? [productsQuery.data[0]?.id ?? ""] : [],
        required: hasProducts,
      },
      cost: {
        type: "number",
        initialValue: 0,
        required: true,
      },
      description: {
        type: "text",
        initialValue: "",
      },
    },
    onSubmit: (values) => {
      chargePaymentMutation.mutate({
        enduserId: contactId,
        stripeKey: values.stripeAccountKey,
        productIds: values.type === "products" ? values.productIds : undefined,
        cost: {
          currency: "USD",
          amount: values.cost,
        },
        description: values.description,
      });
    },
    validators: {
      cost: (value) => (value > 0 ? undefined : t("Cost must be greater than zero")),
    },
    sideEffects: {
      type: (value, allValues) => ({
        cost:
          value === t("Custom Price")
            ? 0
            : allValues.productIds.reduce<number>((acc, curr) => {
                const product = productsQuery.data?.find((p) => p.id === curr);
                if (product?.cost?.amount) {
                  acc += product.cost.amount;
                }
                return acc;
              }, 0),
      }),
      productIds: (value) => ({
        cost: value.reduce<number>((acc, curr) => {
          const product = productsQuery.data?.find((p) => p.id === curr);
          if (product?.cost?.amount) {
            acc += product.cost.amount;
          }
          return acc;
        }, 0),
      }),
    },
  });

  return (
    <form className="flex flex-col h-full overflow-y-auto p-2" {...form.getFormProps()}>
      <div className="flex-1 grow-1 space-y-2">
        <div className="space-y-2 p-1 w-1/2">
          <Label htmlFor="stripeAccount">{t("Stripe Account")}</Label>
          <Select {...form.getCustomFieldProps("stripeAccountKey")}>
            <SelectTrigger id="stripeAccount" className="w-full">
              <SelectValue placeholder={t("Select Stripe Account")} />
            </SelectTrigger>
            <SelectContent>
              {stripeAccountsQuery.data?.map(({ key, title }) => (
                <SelectItem key={key} value={key}>
                  {title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!!form.values.stripeAccountKey && (
          <>
            <div className="border-t w-full" />
            <TabbedContainer
              tabs={tabs}
              showCard={false}
              onValueChange={(newVal) => form.setFieldValue("type", newVal)}
              tabsClassName="mt-2"
            >
              <div className="flex flex-col gap-4">
                <TabsContent value={t("Products")}>
                  <div className="space-y-2 p-1 max-w-full">
                    <Label htmlFor="products">{t("Select Products")}</Label>
                    <div className="flex gap-4 items-center max-w-full">
                      <MultiSelect
                        id="products"
                        options={
                          productsQuery.data?.map(({ title, id }) => ({
                            label: title,
                            value: id,
                          })) ?? []
                        }
                        {...form.getCustomFieldProps("productIds")}
                        placeholder={t("Select Products")}
                        className="grow flex-1"
                      />

                      <div className="flex flex-1 gap-1 grow-0">
                        <p className="font-bold text-foreground min-w-max">{t("Total Cost: ")}</p>
                        <p className="text-muted-foreground">
                          {`${getCurrencySymbol("USD")}${form.values.cost.toFixed(2)}`}
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value={t("Custom Price")}>
                  <div className="flex gap-2">
                    <div className="space-y-2 p-1 grow">
                      <Label htmlFor="customCost">{t("Cost")}</Label>
                      <CurrencyInput
                        id="customCost"
                        placeholder={t("Cost")}
                        {...form.getFieldProps("cost")}
                        onChange={(value) => form.setFieldValue("cost", value)}
                      />
                    </div>
                    <div className="space-y-2 p-1 w-1/2">
                      <Label htmlFor="currency">{t("Currency")}</Label>
                      <Select value="USD">
                        <SelectTrigger id="stripeAccount" className="w-full">
                          <SelectValue placeholder={t("Select Stripe Account")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">{t("USD")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>
                <div className="space-y-2 p-1 max-w-full">
                  <Label htmlFor="purchaseDescription">{t("Purchase Description")}</Label>
                  <Input
                    id="purchaseDescription"
                    placeholder={t("Enter a Purchase Description")}
                    {...form.getFieldProps("description")}
                  />
                </div>
              </div>
            </TabbedContainer>
          </>
        )}
      </div>
      <div className="w-full flex justify-end space-x-2">
        <Button variant="outline" onClick={onCancel}>
          {t("Cancel")}
        </Button>
        <Button disabled={!form.isValid} type="submit">
          {t("Charge Card on File")}
        </Button>
      </div>
    </form>
  );
};
